import PageLayout from "pages/layout/PageLayout"
import { useAppSelector } from "hooks/useApp"
import { useEffect, useState, useCallback } from "react"
import { getShareKeyProjectMiddleware } from "./project-share-organization.api"
import { Avatar } from "components/Avatar"
import { pushTo } from "helpers/history"
import { PATH } from "constants/path"
import Button from "components/Button/Button"
import { Pagination } from "components/Pagination/Pagination"
import { useBoolean, useNumber, useString, useWindowSize } from "helpers/hooks"
import { debounce } from "lodash"
import { ReactComponent as IconSearchNormal } from "assets/images/icons/icon-search-normal.svg"
import InputDefault from "components/Input/InputDefault"
import { ReactComponent as IconClearSearch } from "assets/images/icons/icon-clear-search.svg"
import { ShareKeyProjectModal } from "./modal/ShareKeyProjectModal"
import { EncryptionKeys } from "controllers/EncryptionController"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { KeyEncryptionType } from "types"
import { getPrivateKeyByUserId } from "helpers/utils"
import { hasPermission } from "helpers/permissions"
import { PERMISSIONS } from "constants/permissions"

export interface SelectedUser {
  avatar: string
  fullname: string
  email: string
  id: string
  projectKeyIdTotal: number
  projectKeyIds: string[]
  publicKey: string
  role_name: string
}
export interface ShareKeyProject {
  projectKey: string
  projectId: string
}
export const ShareKeyOrganizationPage = () => {
  const userInfo = useAppSelector((state) => state.userInfo)
  const { isMobile } = useWindowSize()
  const [shareKeyProject, setShareKeyProject] = useState<{
    users: SelectedUser[]
    totalUsers: number
    totalProjectKeys: number
    projectKeys: ShareKeyProject[]
  }>({
    users: [],
    totalUsers: 0,
    totalProjectKeys: 0,
    projectKeys: [],
  })

  // Pagination states
  const [allUsers, setAllUsers] = useState<SelectedUser[]>([])
  const [paginationState, setPaginationState] = useState({
    page: 1,
    pageSize: 25,
  })
  const totalPage = useNumber(0)
  const search = useString()
  const isOpenShareKeyProjectModal = useBoolean(false)
  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([])
  const isShareAllKeys = useBoolean(false)

  useEffect(() => {
    if (!userInfo?.id) {
      return
    }
    if (!userInfo.last_organization_id) {
      pushTo(PATH.notWorkspace)
      return
    }
    const isFullAccess = hasPermission(
      userInfo,
      PERMISSIONS.PROJECT_FULL_ACCESS
    )
    if (!isFullAccess) {
      pushTo(PATH.projects)
      return
    }
    getData()
  }, [userInfo?.id])

  const getData = async () => {
    try {
      const response = await getShareKeyProjectMiddleware()
      if (response) {
        const decryptedKeys: ShareKeyProject[] = []
        if (response?.projectKeys && response?.projectKeys.length) {
          const userVaultKey = localStorage.getItem(
            EncryptionKeys.userVaultKey
          ) as string
          const privateKeyPem: any = await getPrivateKeyByUserId()

          for (const el of response.projectKeys) {
            let decrypted = el.encryptedKey
            if (el.type === KeyEncryptionType.ProjectShareKey) {
              decrypted = privateKeyPem.decrypt(el.encryptedKey, "RSA-OAEP")
            } else {
              decrypted = await encryptionGCMHelper.decryptContext(
                userVaultKey,
                el.encryptedKey
              )
            }
            if (decrypted && decrypted !== el.encryptedKey) {
              decryptedKeys.push({
                projectKey: decrypted,
                projectId: el.id,
              })
            }
          }
        }
        setShareKeyProject({
          projectKeys: decryptedKeys,
          totalProjectKeys: decryptedKeys.length,
          users: response.users || [],
          totalUsers: response.users?.length || 0,
        })
        setAllUsers(response.users || [])
        totalPage.setValue(response.users?.length || 0)
      } else {
        pushTo(PATH.projects)
      }
    } catch (error) {
      pushTo(PATH.projects)
    }
  }

  const handleChangePage = (newPage: number, newLimit: number) => {
    setPaginationState({
      page: newPage,
      pageSize: newLimit,
    })
  }

  const onPreventBubble = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearch = useCallback(
    debounce((newSearch: string) => {
      // Filter users based on search term
      const filteredUsers = allUsers.filter((user) => {
        const searchTerm = newSearch.toLowerCase().trim()
        return (
          user.fullname?.toLowerCase().includes(searchTerm) ||
          user.email?.toLowerCase().includes(searchTerm)
        )
      })

      setShareKeyProject((prev) => ({
        ...prev,
        users: filteredUsers,
      }))
      totalPage.setValue(filteredUsers.length)
      setPaginationState((prev) => ({
        ...prev,
        page: 1,
      }))
    }, 500),
    [allUsers]
  )

  const handleShareKey = (users: SelectedUser[]) => {
    isOpenShareKeyProjectModal.setValue(true)
    setSelectedUsers(users)
    isShareAllKeys.setValue(false)
  }

  const handleShareKeyDone = () => {
    isOpenShareKeyProjectModal.setValue(false)
    search.setValue("")
    setPaginationState({
      page: 1,
      pageSize: 25,
    })
    getData()
  }

  const handleShareAllKeys = () => {
    // Filter users who don't have all keys yet
    const usersNeedingKeys = allUsers.filter(
      (user) => user.projectKeyIdTotal < shareKeyProject.totalProjectKeys
    )

    if (usersNeedingKeys.length === 0) {
      return // No users need keys
    }

    isOpenShareKeyProjectModal.setValue(true)
    isShareAllKeys.setValue(true)
    setSelectedUsers(usersNeedingKeys)
  }

  // Check if all users have all keys
  const isAllUsersHaveAllKeys = () => {
    return allUsers.every(
      (user) => user.projectKeyIdTotal === shareKeyProject.totalProjectKeys
    )
  }

  return (
    <PageLayout
      childrenHeader={
        <div
          className="flex items-center justify-between h-full px-[24px]"
          style={{
            paddingBottom: isMobile ? 12 : 0,
            paddingTop: isMobile ? 12 : 0,
            flexDirection: isMobile ? "column" : undefined,
            alignItems: isMobile ? "start" : undefined,
            gap: isMobile ? 12 : undefined,
          }}
        >
          <h2 className="text-[16px] font-semibold lh-26 text-black whitespace-nowrap">
            Share Key Project
          </h2>
          <div
            className="flex items-center gap-[12px]"
            style={{
              justifyContent: isMobile ? "space-between" : undefined,
              width: isMobile ? "100%" : undefined,
            }}
          >
            <div
              className=" bg-grayWhite2 p-2 rounded-full flex items-center"
              onClick={onPreventBubble}
              style={{
                width: isMobile ? 200 : 320,
              }}
            >
              <IconSearchNormal
                style={{
                  width: 20,
                }}
                values={search.value}
                onClick={onPreventBubble}
                onChange={(e) => {
                  onPreventBubble(e)
                }}
              />
              <InputDefault
                value={search.value}
                placeholder="Search by name or email..."
                style={{
                  border: "none",
                  boxShadow: "none",
                  height: 30,
                  fontWeight: 400,
                }}
                classCustom="h-11 border rounded bg-grayWhite2 pl-3 pr-3 text-sm font-light text-[#111111] focus:outline-none"
                onClick={onPreventBubble}
                onChange={(e) => {
                  onPreventBubble(e)
                  search.setValue(e.target.value)
                  debounceSearch(e.target.value)
                }}
              />
              {search.value ? (
                <div
                  onClick={(e) => {
                    onPreventBubble(e)
                    search.setValue("")
                    // Reset to show all users
                    setShareKeyProject((prev) => ({
                      ...prev,
                      users: allUsers,
                    }))
                    totalPage.setValue(allUsers.length)
                    setPaginationState((prev) => ({
                      ...prev,
                      page: 1,
                    }))
                  }}
                  className="h-5 w-5 flex items-center justify-center custom-icon-clear-search cursor-pointer"
                >
                  <IconClearSearch />
                </div>
              ) : null}
            </div>
            <Button
              widthBtn={150}
              isDisabledBtn
              disabled={isAllUsersHaveAllKeys()}
              title="Share all keys"
              onClick={handleShareAllKeys}
            />
          </div>
        </div>
      }
    >
      <div className="flex flex-col p-6">
        {(() => {
          // Client-side pagination
          const { page, pageSize } = paginationState
          const startIndex = (page - 1) * pageSize
          const endIndex = startIndex + pageSize
          const paginatedUsers = shareKeyProject.users.slice(
            startIndex,
            endIndex
          )

          return paginatedUsers.length
            ? paginatedUsers.map((user) => {
                return (
                  <div
                    key={user.id}
                    className="grid grid-cols-2 border border-[#E4E4E4] p-[12px] rounded-[6px] mb-[12px] bg-[#FFFFFF]"
                    style={{
                      gridTemplateColumns: isMobile ? "1fr" : undefined,
                      gap: isMobile ? 12 : undefined,
                    }}
                  >
                    <div className="flex items-center">
                      <Avatar
                        width="24px"
                        height="24px"
                        name={user.fullname || user.email}
                        avatar={user?.avatar}
                        textcolor="white"
                        contentsize="11px"
                      />

                      <p className="font-semibold overflow-hidden text-ellipsis text-[#111111] text-[13px] ml-[12px] leading-[22px]">
                        {user?.fullname || "N/A"}
                      </p>
                      <div className="h-[4px] min-w-[4px] mx-2 bg-[#7A7A7A] rounded-full" />
                      <p className="font-normal text-[13px] leading-[22px] text-[#7A7A7A]">
                        {user.email}
                      </p>
                    </div>
                    <div className="grid grid-cols-3">
                      <div>
                        <p className="font-semibold  text-[#111111] text-[13px] leading-[22px]">
                          {user.role_name}
                        </p>
                      </div>
                      <div className="flex items-center justify-center">
                        <p className="font-normal text-[13px] leading-[22px] text-[#7A7A7A]">
                          Key:
                          <span className="font-semibold text-[#111111]">
                            {` ${user.projectKeyIdTotal}`}/
                            {shareKeyProject.totalProjectKeys}
                          </span>
                        </p>
                      </div>
                      <div className="flex items-center justify-end">
                        {user.projectKeyIdTotal <
                        shareKeyProject.totalProjectKeys ? (
                          <Button
                            title="Share key"
                            sizeBtn="small"
                            onClick={() => {
                              handleShareKey([user])
                            }}
                            colorBtn="white"
                            styleButton={{
                              minWidth: 100,
                            }}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                )
              })
            : null
        })()}
        {totalPage.value ? (
          <Pagination
            total={totalPage.value}
            queryState={{
              page: paginationState.page,
              limit: paginationState.pageSize,
            }}
            handleChangePage={handleChangePage}
          />
        ) : null}
      </div>
      {isOpenShareKeyProjectModal.value && (
        <ShareKeyProjectModal
          onCloseModal={() => isOpenShareKeyProjectModal.setValue(false)}
          projectKeys={shareKeyProject.projectKeys}
          selectedUsers={selectedUsers}
          isShareAllKeys={isShareAllKeys.value}
          onDone={handleShareKeyDone}
          isOpen={isOpenShareKeyProjectModal.value}
        />
      )}
    </PageLayout>
  )
}
